import Experience from '@root/agents.joinroot.com/src/models/experiences';
import NetworkService from '@root/core/src/services/network-service';
import PropTypes from '@root/vendor/prop-types';
import SceneLoader from '@root/core/src/components/scene-loader';
import { authenticateAgent } from '@root/agents.joinroot.com/src/api/root-server';
import { createBridgeLinkWithExperienceBasedAttribution } from '@root/agents.joinroot.com/src/api/create-agency-platform-bridge-link';
import { useCallback, useEffect, useMemo } from '@root/vendor/react';
import { useHistory, useLocation } from '@root/vendor/react-router-dom';
import { useLogger } from '@root/agents.joinroot.com/src/utils/logging';

const REDIRECTS = {
  login: 'login',
  bridge: 'bridge',
};

// TODO: This is a temporary fix to prevent multiple authentication attempts
let hasAttemptedAuth = false;
export function resetHasAttemptedAuth() {
  hasAttemptedAuth = false;
}

export default function Authenticate({ setAccessToken, logOut }) {
  const logger = useLogger();
  const location = useLocation();
  const history = useHistory();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const agentId = searchParams.get('agent_id');
  const token = searchParams.get('token');
  const quoteId = searchParams.get('quote_id');
  const quoteSource = searchParams.get('quote_source');
  const experience = searchParams.get('experience');

  const redirectToLogin = useCallback(() => {
    logOut(); // Log out first to ensure they are really asked to log in again
    history.push('/login');
  }, [history, logOut]);

  const redirectToBridge = useCallback(async (authenticatedAgentId) => {
    try {
      // Default to agent_customer_onboarding if experience is not specified or invalid
      const targetExperience = experience === Experience.AgentEndorsement
        ? Experience.AgentEndorsement
        : Experience.AgentCustomerOnboarding;
      const bridgeLink = await createBridgeLinkWithExperienceBasedAttribution(
        quoteId,
        authenticatedAgentId,
        quoteSource,
        targetExperience
      );
      // eslint-disable-next-line root/prevent-use-of-window-location
      window.location.assign(bridgeLink);
    } catch (bridgeError) {
      logger.error('Authenticate: Error creating bridge link', {
        error: bridgeError,
        authenticatedAgentId,
        quoteId,
      });
      redirectToLogin();
    }
  }, [quoteId, quoteSource, experience, logger, redirectToLogin]);

  useEffect(() => {
    const attemptAuthentication = async () => {
      if (hasAttemptedAuth) {
        return;
      }
      hasAttemptedAuth = true;

      const result = await NetworkService.request(authenticateAgent, {
        agentId,
        token,
        quoteId,
      });

      if (result.isSuccess && result.data) {
        const {
          accessToken, redirect, authenticatedAgentId,
        } = result.data;

        if (accessToken) {
          setAccessToken(accessToken);
        }

        if (redirect === REDIRECTS.bridge) {
          await redirectToBridge(authenticatedAgentId);
        } else if (redirect === REDIRECTS.login) {
          redirectToLogin();
        }
      } else {
        logger.error('Authenticate: Authentication failed', {
          agentId,
          quoteId,
          hasData: !!result.data,
        });

        redirectToLogin();
      }
    };

    attemptAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies because this should only ever run once on mount

  return <SceneLoader />;
}

Authenticate.propTypes = {
  logOut: PropTypes.func.isRequired,
  setAccessToken: PropTypes.func.isRequired,
};
